// SpotlightTour — premium discovery overlay.
//
// Renders a full-screen darkened layer with a soft cutout around the current
// target element. Non-focused UI is dimmed (the rest of the screen) and the
// target receives a gentle scale + glow ring. Tap anywhere advances; an
// always-visible "Skip" closes the tour.
//
// The overlay is purely SVG-driven (mask-based cutout) so the highlighted
// element stays interactive AT NO POINT — the mask is just visual. We pass
// `pointer-events: auto` only on the dim layer; the cutout is "see-through"
// for visuals only.
//
// Implementation notes:
//   - Targets are looked up via [data-spotlight-id="..."] each step so we
//     handle late-mounting elements (e.g. MomentumHero finishing its fetch).
//   - We poll the rect via requestAnimationFrame for the lifetime of the
//     step — handles scroll, layout shifts and viewport resize without a
//     ResizeObserver dance.
//   - First step also auto-scrolls the target into view (smooth).
//   - Reduced-motion users get instant transitions.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X, ArrowRight } from "lucide-react";

import { TOURS } from "./tours";
import { useSpotlight, hasSeen, markSeen, endTour } from "./useSpotlight";

function useTargetRect(targetId, padding) {
    const [rect, setRect] = useState(null);
    useLayoutEffect(() => {
        if (!targetId) { setRect(null); return; }
        let raf = 0;
        let cancelled = false;
        const tick = () => {
            if (cancelled) return;
            const el = document.querySelector(`[data-spotlight-id="${targetId}"]`);
            if (el) {
                const r = el.getBoundingClientRect();
                setRect((prev) => {
                    // Only update on real change to avoid noisy re-renders.
                    if (prev && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.left - r.left) < 0.5
                        && Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5) return prev;
                    return {
                        top: r.top - padding,
                        left: r.left - padding,
                        width: r.width + padding * 2,
                        height: r.height + padding * 2,
                    };
                });
            } else {
                setRect(null);
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => { cancelled = true; cancelAnimationFrame(raf); };
    }, [targetId, padding]);
    return rect;
}

export default function SpotlightTour() {
    const { tour } = useSpotlight();
    const { i18n } = useTranslation();
    const lang = i18n.language?.startsWith("nl") ? "nl" : "en";

    const steps = useMemo(() => (tour ? (TOURS[tour.id] || []) : []), [tour]);
    const [idx, setIdx] = useState(0);

    // Reset whenever a new tour starts
    useEffect(() => { setIdx(0); }, [tour?.ts]);

    // Always close the wheel when the tour itself ends so the user lands
    // back on the rings view rather than a stuck-open overlay.
    useEffect(() => {
        if (!tour) {
            window.dispatchEvent(new Event("wheel-reveal-close"));
        }
    }, [tour]);

    const current = steps[idx];
    const padding = current?.padding ?? 12;
    const rect = useTargetRect(current?.target, padding);

    // Smooth-scroll the target into view at the start of each step. We center
    // the target around 35% from the top so the copy card has room either
    // above or below it. Runs every step transition.
    useEffect(() => {
        if (!current) return;
        const el = document.querySelector(`[data-spotlight-id="${current.target}"]`);
        if (!el) return;

        // For wheel-related steps we ALSO programmatically slide the
        // wheel layer into view — otherwise the demo talks about the wheel
        // while only the activity rings are visible. The MomentumWheelLayer
        // listens for these events and animates revealPct accordingly.
        if (current.target === "wheel-handle") {
            window.dispatchEvent(new Event("wheel-reveal-open"));
        } else {
            window.dispatchEvent(new Event("wheel-reveal-close"));
        }

        // Defer one frame so any layout that just changed (route nav, modal
        // open) has settled before we measure.
        const t = requestAnimationFrame(() => {
            try {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch {
                const r = el.getBoundingClientRect();
                window.scrollTo({
                    top: Math.max(0, window.scrollY + r.top - window.innerHeight * 0.35),
                    behavior: "smooth",
                });
            }
        });
        return () => cancelAnimationFrame(t);
    }, [current?.target]); // eslint-disable-line react-hooks/exhaustive-deps

    // Tour is active — keep the page interactive (the overlay traps clicks
    // already, and the cutout-rect tracker re-aligns the spotlight on every
    // frame so any incidental scroll stays in sync). Locking body scroll
    // would block our own scrollIntoView calls between steps.

    const finish = useCallback(() => {
        if (tour?.id) markSeen(tour.id);
        endTour();
    }, [tour]);

    const next = useCallback(() => {
        if (idx + 1 >= steps.length) finish();
        else setIdx((i) => i + 1);
    }, [idx, steps.length, finish]);

    if (!tour || !current) return null;

    const copy = current[lang] || current.en;

    // Decide where to place the copy card relative to the cutout.
    // If target is in the lower half → place card above it. Else below.
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const placeAbove = rect ? (rect.top + rect.height / 2) > vh / 2 : false;

    // SVG mask: full screen rect with the cutout subtracted.
    const W = typeof window !== "undefined" ? window.innerWidth : 400;
    const H = vh;

    return createPortal(
        <div
            className="fixed inset-0 z-[200] spotlight-fade-in"
            data-testid="spotlight-overlay"
            onClick={next}
        >
            {/* Dim + blur layer with cutout via SVG mask */}
            <svg
                width="100%"
                height="100%"
                className="absolute inset-0 pointer-events-none"
                aria-hidden="true"
            >
                <defs>
                    <mask id="spotlight-mask">
                        <rect x="0" y="0" width={W} height={H} fill="white" />
                        {rect && (
                            <rect
                                x={rect.left}
                                y={rect.top}
                                width={rect.width}
                                height={rect.height}
                                rx={current.radius}
                                ry={current.radius}
                                fill="black"
                            />
                        )}
                    </mask>
                    <radialGradient id="spotlight-vignette" cx="50%" cy="50%" r="65%">
                        <stop offset="0%"  stopColor="rgba(0,0,0,0.55)" />
                        <stop offset="100%" stopColor="rgba(0,0,0,0.85)" />
                    </radialGradient>
                </defs>
                <rect x="0" y="0" width={W} height={H} fill="url(#spotlight-vignette)" mask="url(#spotlight-mask)" />
            </svg>

            {/* Glow ring around the cutout */}
            {rect && (
                <div
                    className="absolute pointer-events-none spotlight-ring"
                    style={{
                        top:    rect.top,
                        left:   rect.left,
                        width:  rect.width,
                        height: rect.height,
                        borderRadius: current.radius,
                    }}
                    data-testid="spotlight-ring"
                />
            )}

            {/* Skip button — always visible top-right */}
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); finish(); }}
                data-testid="spotlight-skip"
                aria-label={lang === "nl" ? "Sla over" : "Skip"}
                className="absolute top-4 right-4 z-10 rounded-full px-4 py-2 text-xs font-semibold
                           bg-white/10 hover:bg-white/20 text-white border border-white/15
                           backdrop-blur-md transition flex items-center gap-1.5"
                style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top, 0))" }}
            >
                <X size={12} strokeWidth={2.4} />
                {lang === "nl" ? "Sla over" : "Skip"}
            </button>

            {/* Copy card — pinned above or below the cutout */}
            <div
                className={`absolute left-1/2 -translate-x-1/2 max-w-sm w-[calc(100%-2rem)]
                            spotlight-card-${placeAbove ? "above" : "below"}`}
                style={
                    placeAbove
                        ? { bottom: rect ? Math.max(24, H - rect.top + 20) : 60 }
                        : { top:    rect ? rect.top + rect.height + 20 : 60 }
                }
                onClick={(e) => e.stopPropagation()}
                data-testid="spotlight-card"
            >
                <div className="rounded-3xl border border-white/15 bg-black/60 backdrop-blur-2xl
                                px-5 py-5 text-white shadow-2xl shadow-black/60">
                    <h3 className="font-display text-lg font-semibold tracking-tight" data-testid="spotlight-title">
                        {copy.title}
                    </h3>
                    <p className="text-[13.5px] leading-relaxed text-white/75 mt-1.5" data-testid="spotlight-subtitle">
                        {copy.subtitle}
                    </p>

                    <div className="mt-4 flex items-center justify-between">
                        {/* Step dots */}
                        <div className="flex items-center gap-1.5" data-testid="spotlight-dots">
                            {steps.map((_, i) => (
                                <span
                                    key={i}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                        i === idx ? "w-5 bg-white" : "w-1.5 bg-white/30"
                                    }`}
                                />
                            ))}
                        </div>

                        <button
                            type="button"
                            onClick={next}
                            data-testid="spotlight-next"
                            className="rounded-full bg-white text-black px-4 py-2 text-xs font-semibold
                                       flex items-center gap-1.5 active:scale-95 transition"
                        >
                            {idx + 1 < steps.length
                                ? (lang === "nl" ? "Volgende" : "Next")
                                : (lang === "nl" ? "Klaar"     : "Done")}
                            <ArrowRight size={12} strokeWidth={2.4} />
                        </button>
                    </div>

                    {idx === 0 && steps.length > 1 && (
                        <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/40 mt-3 text-center">
                            {lang === "nl" ? "Tik om door te gaan" : "Tap to continue"}
                        </p>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

// Convenience: auto-runs the first-login tour once the dashboard is ready.
// Returns a function that callers (like Dashboard) can invoke after mount
// once their critical content is on screen.
export function useFirstLoginTour(tourId, { delay = 700, when = true } = {}) {
    const { startTour } = useSpotlight();
    useEffect(() => {
        if (!when) return;
        if (hasSeen(tourId)) return;
        const t = setTimeout(() => startTour(tourId), delay);
        return () => clearTimeout(t);
    }, [tourId, when, delay, startTour]);
}
